import type { EscalationEvent } from "@redotech/merchant-sdk/rpc/schema/agents/merchant-support-agent/escalation";
import { useRequiredContext } from "@redotech/react-util/context";
import { sinkPromise } from "@redotech/util/promise";
import { sleep } from "@redotech/util/schedule";
import { useCallback, useEffect, useRef, useState } from "react";
import { RedoMerchantRpcClientContext } from "../rpc-client";
import { EscalationStatus, useEscalationContext } from "./escalation-context";

export interface HumanMessage {
  id: string;
  body: string;
  authorName: string | null;
  authorId: string;
  authorAvatarUrl: string | null;
  createdAt: number;
  attachments: { name?: string; url: string; contentType?: string }[];
}

export interface UseEscalationSubscriptionResult {
  messages: HumanMessage[];
  status: EscalationStatus;
  isConnected: boolean;
}

// Reconnection settings
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 10;

/**
 * Hook to subscribe to escalation updates via real-time streaming.
 * Uses the Notice server SSE through the watchEscalationEvents RPC.
 * Automatically reconnects with exponential backoff on disconnection.
 */
export function useEscalationSubscription(
  onNewMessage?: (message: HumanMessage) => void,
  onStatusChange?: (status: EscalationStatus) => void,
): UseEscalationSubscriptionResult {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { escalation, setEscalation } = useEscalationContext();

  const [messages, setMessages] = useState<HumanMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  const messageIdsRef = useRef<Set<string>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isCleaningUpRef = useRef(false);
  const isConnectingRef = useRef(false);
  const escalationIdRef = useRef(escalation.escalationId);
  const escalationStatusRef = useRef(escalation.status);
  const prevEscalationIdRef = useRef<string | null>(null);

  // Store callbacks in refs to avoid dependency changes causing reconnects
  const onNewMessageRef = useRef(onNewMessage);
  const onStatusChangeRef = useRef(onStatusChange);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  // Keep escalation refs in sync - these are read inside connectToStream
  // to avoid dependency changes causing reconnection loops
  useEffect(() => {
    escalationIdRef.current = escalation.escalationId;
  }, [escalation.escalationId]);

  useEffect(() => {
    escalationStatusRef.current = escalation.status;
  }, [escalation.status]);

  const addMessage = useCallback((event: EscalationEvent) => {
    if (event.type !== "human-message" || !event.messageId || !event.authorId) {
      return;
    }

    // Deduplicate by message ID
    if (messageIdsRef.current.has(event.messageId)) {
      return;
    }
    messageIdsRef.current.add(event.messageId);

    const message: HumanMessage = {
      id: event.messageId,
      body: event.body ?? "",
      authorName: event.authorName ?? null,
      authorId: event.authorId,
      authorAvatarUrl: event.authorAvatarUrl ?? null,
      createdAt: event.createdAt ?? Date.now() / 1000,
      attachments: event.attachments ?? [],
    };

    setMessages((prev) => [...prev, message]);
    onNewMessageRef.current?.(message);
  }, []);

  const handleStatusChange = useCallback(
    (newStatus: EscalationStatus) => {
      setEscalation({ status: newStatus });
      onStatusChangeRef.current?.(newStatus);
    },
    [setEscalation],
  );

  const connectToStream = useCallback(async () => {
    // Use refs to get current values without causing dependency changes
    const currentEscalationId = escalationIdRef.current;
    const currentStatus = escalationStatusRef.current;

    if (!currentEscalationId) {
      return;
    }

    // Don't connect if already closed or idle
    if (currentStatus === "idle" || currentStatus === "closed") {
      return;
    }

    // Don't connect if cleaning up
    if (isCleaningUpRef.current) {
      return;
    }

    // Prevent multiple simultaneous connection attempts (React Strict Mode)
    if (isConnectingRef.current) {
      return;
    }
    isConnectingRef.current = true;

    // Abort any existing connection
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const streamGenerator = merchantRpcClient.watchEscalationEvents(
        { escalationId: currentEscalationId },
        { signal: abortController.signal },
      );

      for await (const event of streamGenerator) {
        if (abortController.signal.aborted) {
          break;
        }

        switch (event.type) {
          case "connection-made":
            setIsConnected(true);
            reconnectAttemptsRef.current = 0;
            break;

          case "human-message":
            addMessage(event);
            break;

          case "status-change":
            if (event.status) {
              handleStatusChange(event.status as EscalationStatus);
            }
            break;

          case "escalation-closed":
            handleStatusChange("closed");
            setIsConnected(false);
            return; // Stop streaming

          case "error":
            // Error event received, handled gracefully
            break;
        }
      }

      // Stream ended normally (timeout), reconnect
      isConnectingRef.current = false;
      if (!abortController.signal.aborted && !isCleaningUpRef.current) {
        reconnectAttemptsRef.current = 0;
        await sleep(Temporal.Duration.from({ seconds: 1 }));
        sinkPromise(connectToStream());
      }
    } catch (error) {
      isConnectingRef.current = false;
      setIsConnected(false);

      // Handle AbortError gracefully
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }

      // Don't reconnect if cleaning up
      if (isCleaningUpRef.current) {
        return;
      }

      // Stop reconnecting after max attempts
      if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
        return;
      }

      // Exponential backoff for reconnection
      const delay = Math.min(
        INITIAL_RECONNECT_DELAY_MS * 2 ** reconnectAttemptsRef.current,
        MAX_RECONNECT_DELAY_MS,
      );
      reconnectAttemptsRef.current++;

      // Reconnecting after error with exponential backoff
      await sleep(Temporal.Duration.from({ milliseconds: delay }));
      sinkPromise(connectToStream());
    }
  }, [merchantRpcClient, addMessage, handleStatusChange]);

  // Start/stop streaming based on escalation ID
  // Note: We only depend on escalationId, not status, to avoid reconnections
  // when status changes during an active escalation. The stream handles
  // status changes internally via events.
  useEffect(() => {
    if (!escalation.escalationId) {
      setIsConnected(false);
      return;
    }

    // Check current status via ref to avoid dependency
    const currentStatus = escalationStatusRef.current;
    if (currentStatus === "idle" || currentStatus === "closed") {
      setIsConnected(false);
      return;
    }

    isCleaningUpRef.current = false;
    sinkPromise(connectToStream());

    return () => {
      isCleaningUpRef.current = true;
      isConnectingRef.current = false;
      setIsConnected(false);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [escalation.escalationId, connectToStream]);

  // Reset messages when escalation changes (either to null or to a different ID)
  useEffect(() => {
    const currentId = escalation.escalationId;
    const prevId = prevEscalationIdRef.current;

    // Clear on any change (null or different ID)
    if (currentId !== prevId) {
      setMessages([]);
      messageIdsRef.current.clear();
      reconnectAttemptsRef.current = 0;
      isConnectingRef.current = false;
    }

    prevEscalationIdRef.current = currentId;
  }, [escalation.escalationId]);

  return { messages, status: escalation.status, isConnected };
}
